import React, { useState } from "react";
import { useApp } from "../context/AppContext";
import { Accounts } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Label } from "./ui/label";
import { Receipt, Pencil } from "lucide-react";
import { toast } from "sonner";
import { cn } from "../lib/utils";

/** High-visibility banner that surfaces the Billing Ledger Name set by Accounts
 *  on every downstream surface (Sales Detail, Delivery File, Spares, VAS).
 *  Only Accounts / Admin may edit — everyone else sees it read-only. */
export function BillingLedgerBanner({ sale, onUpdated, compact = false, className }) {
  const { activeUser } = useApp();
  const ledger = (sale?.accounts?.billing_ledger_name || "").trim();
  const canEdit = activeUser?.role === "Accounts" || activeUser?.role === "Admin";
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(ledger);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => { setValue(ledger); }, [ledger]);

  const onSave = async () => {
    const v = value.trim();
    if (!v) { toast.error("Billing Ledger Name is required"); return; }
    setBusy(true);
    try {
      await Accounts.updateBillingLedger(sale.id, { billing_ledger_name: v, user_id: activeUser?.id });
      toast.success("Billing Ledger updated", { duration: 2000 });
      setOpen(false);
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  // No ledger yet — only show a soft prompt to Accounts/Admin so they capture it.
  if (!ledger) {
    if (!canEdit) return null;
    return (
      <>
        <div
          className={cn(
            "flex items-center gap-3 rounded-md border border-dashed border-slate-300 bg-slate-50 p-3 text-sm text-slate-600",
            className,
          )}
          data-testid="billing-ledger-missing"
        >
          <Receipt className="h-4 w-4 flex-shrink-0 text-slate-500" />
          <span className="flex-1">No Billing Ledger set yet. Capture it before Accounts completion.</span>
          <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="gap-1"
            data-testid="billing-ledger-set-btn">
            <Pencil className="h-3 w-3" /> Set Ledger
          </Button>
        </div>
        <BillingLedgerDialog
          open={open} onOpenChange={setOpen}
          value={value} setValue={setValue} onSave={onSave} busy={busy}
        />
      </>
    );
  }

  // Primary banner
  return (
    <>
      <div
        className={cn(
          "flex items-center gap-3 rounded-md border-2 border-blue-300 bg-blue-50 px-4 py-2.5",
          compact ? "" : "shadow-sm",
          className,
        )}
        data-testid="billing-ledger-banner"
      >
        <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-white">
          <Receipt className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-blue-700">
            Billing Ledger
          </div>
          <div className="truncate font-display text-base font-bold tracking-tight text-blue-900"
            title={ledger} data-testid="billing-ledger-name">
            {ledger}
          </div>
          {!compact && (
            <div className="text-[10px] text-blue-700">
              Use this ledger for all billing — Vehicle invoice, Accessories &amp; VAS.
            </div>
          )}
        </div>
        {canEdit && (
          <Button
            size="sm" variant="outline" onClick={() => setOpen(true)}
            className="gap-1 border-blue-300 text-blue-800 hover:bg-blue-100"
            data-testid="billing-ledger-edit-btn"
          >
            <Pencil className="h-3 w-3" /> Edit
          </Button>
        )}
      </div>
      <BillingLedgerDialog
        open={open} onOpenChange={setOpen}
        value={value} setValue={setValue} onSave={onSave} busy={busy}
      />
    </>
  );
}

/** Compact, label-less edit launcher — opens the same dialog as the banner.
 *  Use this inline next to a Billing Ledger value (e.g., inside summary cards)
 *  so you can keep edit functionality without a heavy banner. */
export function BillingLedgerEditButton({ sale, onUpdated, testid = "billing-ledger-edit" }) {
  const { activeUser } = useApp();
  const ledger = (sale?.accounts?.billing_ledger_name || "").trim();
  const canEdit = activeUser?.role === "Accounts" || activeUser?.role === "Admin";
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(ledger);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => { setValue(ledger); }, [ledger]);

  if (!canEdit) return null;

  const onSave = async () => {
    const v = value.trim();
    if (!v) { toast.error("Billing Ledger Name is required"); return; }
    setBusy(true);
    try {
      await Accounts.updateBillingLedger(sale.id, { billing_ledger_name: v, user_id: activeUser?.id });
      toast.success("Billing Ledger updated", { duration: 2000 });
      setOpen(false);
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-primary hover:underline"
        data-testid={testid}
      >
        <Pencil className="h-3 w-3" /> {ledger ? "Edit" : "Set"}
      </button>
      <BillingLedgerDialog
        open={open} onOpenChange={setOpen}
        value={value} setValue={setValue} onSave={onSave} busy={busy}
      />
    </>
  );
}

function BillingLedgerDialog({ open, onOpenChange, value, setValue, onSave, busy }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-blue-600" /> Billing Ledger Name
          </DialogTitle>
          <DialogDescription>
            All downstream billing (Vehicle invoice, Accessories, VAS) will use this ledger.
            Examples: customer name, finance company ledger, corporate ledger.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">Ledger Name *</Label>
          <Input
            value={value} onChange={(e) => setValue(e.target.value)}
            placeholder="e.g. ABC Corporation / HDFC Bank Ltd / Customer Name"
            data-testid="billing-ledger-input"
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSave} disabled={busy || !value.trim()}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="billing-ledger-save-btn">
            {busy ? "Saving…" : "Save Ledger"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
